import styled, { css } from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import palette from 'lib/styles/palette';
import { BsArrowBarLeft } from 'react-icons/bs';
import { useState } from 'react';
import AskModal from 'components/common/AskModal';
import usePlaceViewer from 'hooks/place/usePlaceViewer';
import { DeleteBtn } from 'lib/styles/icons';
import autoHyphen from 'utils/autoHyphen';
import Loading from 'components/common/Loading';
import ErrorNoti from 'components/common/ErrorNoti';
import usePlan from 'hooks/place/usePlan';
import NotiModal from 'components/common/NotiModal';
import PlanUploader from './PlanUploader';

const Plan = ({ storeId, space, length, getStoreQuery }) => {
  const { onDelete } = usePlan(storeId, space.id, length);
  const [isPlanUploader, setPlanUPloader] = useState(false);

  return (
    <>
      <PlanBlock>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div>
            {space.floor} - {space.name}
          </div>
          <DeleteBtn className="place-viewer-delete-icon" onClick={onDelete} />
          <button
            style={{
              background: palette.theme[2],
              color: 'white',
              padding: '5px 10px',
              marginLeft: '10px',
              borderRadius: '5px',
              border: 'none',
              cursor: 'pointer',
            }}
            onClick={() => setPlanUPloader(true)}
          >
            변경
          </button>
        </div>
        <img className="plan" src={space.filePath} alt="plan" />
      </PlanBlock>
      {isPlanUploader && (
        <PlanUploader
          onClose={() => setPlanUPloader(false)}
          replace
          spaceId={space.id}
          getStoreQuery={getStoreQuery}
        />
      )}
    </>
  );
};

const PlanBlock = styled.div`
  margin-top: 1rem;
  div {
    font-weight: bold;
  }
  img {
    margin-top: 0.5rem;
    width: 100%;
  }
`;

const PlaceViewer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showModal, setShowModal] = useState(false);
  const {
    error,
    loading,
    getStoreQuery,
    store,
    deleteStoreMutation,
    onDelete,
    onEdit,
  } = usePlaceViewer(id);

  if (getStoreQuery.isLoading || deleteStoreMutation.isLoading)
    return <Loading />;
  if (getStoreQuery.error?.response.status === 500 || error)
    return <ErrorNoti />;

  return (
    <>
      {loading.length !== 0 && <Loading />}
      {showModal && (
        <AskModal
          action="삭제"
          message="정말 삭제하시겠습니까?"
          closeModal={() => setShowModal(false)}
          actionFc={() => {
            onDelete();
            setShowModal(false);
          }}
        />
      )}
      {deleteStoreMutation.error?.response.status === 500 && (
        <NotiModal
          closeModal={() => setShowModal(false)}
          msg="오류가 발생하여 삭제에 실패했습니다"
          fontL
        />
      )}
      <PlaceViewerBlock visible={String(loading.length !== 0)}>
        <BsArrowBarLeft className="back-icon" onClick={() => navigate(-1)} />
        <div className="title">{store?.company}</div>
        <div className="subTitle ellipsis" title={store?.name}>
          {store?.name}
        </div>
        <div className="index">1. 기본 정보</div>
        <div className="row">
          <div>주소</div>
          <div>{store?.address}</div>
        </div>
        <div className="row">
          <div>상세 주소</div>
          <div>{store?.detailAddress}</div>
        </div>
        <div className="row">
          <div>관리 코드</div>
          <div>{store?.code}</div>
        </div>
        <div className="row">
          <div>대표 전화</div>
          <div>{autoHyphen(store?.tel)}</div>
        </div>
        <div className="divider" />
        <div className="index">2. 도면 정보</div>
        {store?.space.map(space => (
          <Plan
            key={space.id}
            storeId={id}
            space={space}
            length={store?.space.length}
            getStoreQuery={getStoreQuery}
          />
        ))}
        <div className="divider" />
        <ButtonBlock>
          <StyledButton onClick={() => setShowModal(true)}>삭제</StyledButton>
          <StyledButton blue onClick={onEdit}>
            수정
          </StyledButton>
        </ButtonBlock>
      </PlaceViewerBlock>
    </>
  );
};

export default PlaceViewer;

const PlaceViewerBlock = styled.div`
  width: 480px;
  margin: 0 auto;
  margin-top: 25px;
  padding: 0 15px;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  box-shadow: 0 0 50px 0 rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  position: relative;

  .back-icon {
    position: absolute;
    top: 30px;
    font-size: 1.3rem;
    margin-left: 5px;
    cursor: pointer;
  }

  .place-viewer-delete-icon {
    margin-left: 15px;
    font-size: 20px;
  }

  .title {
    margin-top: 30px;
    font-size: 1.3rem;
    font-weight: bold;
    text-align: center;
  }

  .subTitle {
    width: 120px;
    text-align: right;
    margin-top: 30px;
    font-size: 1.1rem;
    font-weight: bold;
    position: absolute;
    left: 350px;
    top: 5px;
  }

  .index {
    margin-top: 30px;
    font-size: 1.1rem;
    font-weight: 500;
  }

  .row {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
  }

  .divider {
    margin-top: 30px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  ${props =>
    props.visible === 'true' &&
    css`
      display: none;
    `}
`;

const ButtonBlock = styled.div`
  margin-top: 30px;
  display: flex;
  justify-content: center;
`;

const StyledButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background: ${props => (props.blue ? palette.theme[3] : palette.theme[0])};
  color: #fff;
  cursor: pointer;
  :hover {
    background: ${palette.theme[2]};
  }
  & + & {
    margin-left: 10px;
  }
`;
